import { Copy } from 'lucide-react';

import { ThreadHistory } from '@chainlit/react-client';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { Translator } from '../i18n';
import { TimeGroupedThreadList } from './TimeGroupedThreadList';

interface FolderData {
    id: string;
    name: string;
    level: number;
    parentId?: string | null;
}

interface ProjectDialogsProps {
    // Add Folder Dialog
    addFolderOpen: boolean;
    setAddFolderOpen: (open: boolean) => void;
    newFolderName: string;
    setNewFolderName: (name: string) => void;
    onCreateFolder: () => void;
    threadToAssignAfterFolderCreation?: string;
    setThreadToAssignAfterFolderCreation: (
        threadId: string | undefined
    ) => void;

    // Subfolder Dialog
    subfolderParentId?: string;
    setSubfolderParentId: (id: string | undefined) => void;
    subfolderName: string;
    setSubfolderName: (name: string) => void;
    onCreateSubfolder: () => void;

    // Rename Folder Dialog
    folderIdToRename?: string;
    setFolderIdToRename: (id: string | undefined) => void;
    folderNewName: string;
    setFolderNewName: (name: string) => void;
    onRenameFolder: () => void;

    // Delete Folder Dialog
    folderIdToDelete?: string;
    setFolderIdToDelete: (id: string | undefined) => void;
    onDeleteFolder: () => void;

    // Multi-select Dialog
    multiSelectFolderId?: string;
    onCloseMultiSelectDialog: () => void;
    folders: FolderData[];
    threadHistory: ThreadHistory;
    sortedTimeGroupKeys: string[];
    folderedThreadIds: Set<string>;
    getTimeGroupLabel: (group: string) => React.ReactNode;
    selectedThreadIds: Set<string>;
    onToggleThreadSelection: (threadId: string) => void;
    onSelectAllThreads: () => void;
    onDeselectAllThreads: () => void;
    onAssignSelectedThreads: () => void;
    isMobile: boolean;

    // Thread Dialogs
    threadIdToDelete?: string;
    setThreadIdToDelete: (id: string | undefined) => void;
    onDeleteThread: () => void;
    threadIdToRename?: string;
    setThreadIdToRename: (id: string | undefined) => void;
    threadNewName?: string;
    setThreadNewName: (name: string | undefined) => void;
    onRenameThread: () => void;

    // Share Thread Dialog
    threadIdToShare?: string;
    setThreadIdToShare: (id: string | undefined) => void;
    shareUrl?: string;
    onCopyShareUrl: () => void;
}

export function ProjectDialogs({
    addFolderOpen,
    setAddFolderOpen,
    newFolderName,
    setNewFolderName,
    onCreateFolder,
    threadToAssignAfterFolderCreation,
    setThreadToAssignAfterFolderCreation,
    subfolderParentId,
    setSubfolderParentId,
    subfolderName,
    setSubfolderName,
    onCreateSubfolder,
    folderIdToRename,
    setFolderIdToRename,
    folderNewName,
    setFolderNewName,
    onRenameFolder,
    folderIdToDelete,
    setFolderIdToDelete,
    onDeleteFolder,
    multiSelectFolderId,
    onCloseMultiSelectDialog,
    folders,
    threadHistory,
    sortedTimeGroupKeys,
    folderedThreadIds,
    getTimeGroupLabel,
    selectedThreadIds,
    onToggleThreadSelection,
    onSelectAllThreads,
    onDeselectAllThreads,
    onAssignSelectedThreads,
    isMobile,
    threadIdToDelete,
    setThreadIdToDelete,
    onDeleteThread,
    threadIdToRename,
    setThreadIdToRename,
    threadNewName,
    setThreadNewName,
    onRenameThread,
    threadIdToShare,
    setThreadIdToShare,
    shareUrl,
    onCopyShareUrl
}: ProjectDialogsProps) {

    return (
        <>
            {/* Add Folder Dialog */}
            <Dialog
                open={addFolderOpen}
                onOpenChange={(open) => {
                    setAddFolderOpen(open);
                    if (!open) {
                        setNewFolderName('');
                        setThreadToAssignAfterFolderCreation(undefined);
                    }
                }}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Add project</DialogTitle>
                        <DialogDescription>
                            Create a new project.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="my-6">
                        <Label htmlFor="folder-name" className="text-right">
                            Name
                        </Label>
                        <Input
                            className="mt-1"
                            id="folder-name"
                            required
                            value={newFolderName}
                            autoComplete="off"
                            onChange={(e) => setNewFolderName(e.target.value)}
                            autoFocus
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    onCreateFolder();
                                }
                            }}
                        />
                    </div>
                    <DialogFooter>
                        <Button size="medium"
                            type="button"
                            variant="ghost"
                            onClick={() => {
                                setAddFolderOpen(false);
                                setNewFolderName('');
                                setThreadToAssignAfterFolderCreation(undefined);
                            }}
                        >
                            <Translator path="common.actions.cancel" />
                        </Button>
                        <Button size="medium"
                            type="button"
                            onClick={onCreateFolder}
                            disabled={!newFolderName.trim()}
                        >
                            <Translator path="common.actions.confirm" />
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add Subfolder Dialog */}
            <Dialog
                open={!!subfolderParentId}
                onOpenChange={(open) => {
                    if (!open) {
                        setSubfolderParentId(undefined);
                        setSubfolderName('');
                    }
                }}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Add project</DialogTitle>
                        <DialogDescription>
                            Create a sub project.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="my-6">
                        <Label htmlFor="subfolder-name" className="text-right">
                            Name
                        </Label>
                        <Input
                            id="subfolder-name"
                            required
                            value={subfolderName}
                            onChange={(e) => setSubfolderName(e.target.value)}
                            placeholder="e.g. Component Tests"
                            autoFocus
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    onCreateSubfolder();
                                }
                            }}
                        />
                    </div>
                    <DialogFooter>
                        <Button size="medium"
                            type="button"
                            variant="ghost"
                            onClick={() => {
                                setSubfolderParentId(undefined);
                                setSubfolderName('');
                            }}
                        >
                            Cancel
                        </Button>
                        <Button size="medium"
                            type="button"
                            disabled={!subfolderName.trim()}
                            onClick={onCreateSubfolder}
                        >
                            Confirm
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Rename Folder Dialog */}
            <Dialog
                open={!!folderIdToRename}
                onOpenChange={() => setFolderIdToRename(undefined)}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Rename project</DialogTitle>
                        <DialogDescription>
                            Update the project name.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="my-6">
                        <Label htmlFor="folder-rename" className="text-right">
                            Name
                        </Label>
                        <Input
                            id="folder-rename"
                            required
                            value={folderNewName}
                            onChange={(e) => setFolderNewName(e.target.value)}
                            autoFocus
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    onRenameFolder();
                                }
                            }}
                        />
                    </div>
                    <DialogFooter>
                        <Button size="medium"
                            type="button"
                            variant="ghost"
                            onClick={() => setFolderIdToRename(undefined)}
                        >
                            Cancel
                        </Button>
                        <Button size="medium"
                            type="button"
                            disabled={!folderNewName.trim()}
                            onClick={onRenameFolder}
                        >
                            Confirm
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Folder Confirm */}
            <AlertDialog
                open={!!folderIdToDelete}
                onOpenChange={() => setFolderIdToDelete(undefined)}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete project</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will remove the project but keep its chats
                            visible again in the time groups.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="flex-row gap-2 sm:gap-0">
                        <AlertDialogCancel className="mt-0">
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction onClick={onDeleteFolder}>
                            Confirm
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Multi-select Threads Dialog */}
            <Dialog
                open={!!multiSelectFolderId}
                onOpenChange={onCloseMultiSelectDialog}
            >
                <DialogContent className="max-w-2xl max-h-[80vh]">
                    <DialogHeader>
                        <DialogTitle>Add chats to project</DialogTitle>
                        <DialogDescription>
                            Select chats to add to "
                            {
                                folders.find(
                                    (f) => f.id === multiSelectFolderId
                                )?.name
                            }
                            " project.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="my-4">
                        <div className="flex items-center gap-2 mb-4">
                            <Button
                                type="button"
                                variant="tertiary"
                                onClick={onSelectAllThreads}
                                disabled={
                                    sortedTimeGroupKeys.reduce(
                                        (total, group) => {
                                            const items =
                                                threadHistory
                                                    ?.timeGroupedThreads?.[
                                                    group
                                                ] || [];
                                            return (
                                                total +
                                                items.filter(
                                                    (thread) =>
                                                        !folderedThreadIds.has(
                                                            thread.id
                                                        )
                                                ).length
                                            );
                                        },
                                        0
                                    ) === 0
                                }
                            >
                                Select All
                            </Button>
                            <Button
                                type="button"
                                variant="tertiary"
                                onClick={onDeselectAllThreads}
                                disabled={selectedThreadIds.size === 0}
                            >
                                Deselect All
                            </Button>
                            <div className="ml-auto text-(length:--fs-subtitle) tabular-nums text-muted-foreground">
                                {selectedThreadIds.size} of{' '}
                                {sortedTimeGroupKeys.reduce((total, group) => {
                                    const items =
                                        threadHistory?.timeGroupedThreads?.[
                                            group
                                        ] || [];
                                    return (
                                        total +
                                        items.filter(
                                            (thread) =>
                                                !folderedThreadIds.has(
                                                    thread.id
                                                )
                                        ).length
                                    );
                                }, 0)}{' '}
                                selected
                            </div>
                        </div>
                        <div className="border border-gray-200 dark:border-gray-700 rounded-md max-h-[50vh] overflow-y-auto">
                            {sortedTimeGroupKeys.reduce((total, group) => {
                                const items =
                                    threadHistory?.timeGroupedThreads?.[
                                        group
                                    ] || [];
                                return (
                                    total +
                                    items.filter(
                                        (thread) =>
                                            !folderedThreadIds.has(thread.id)
                                    ).length
                                );
                            }, 0) === 0 ? (
                                <div className="py-8 text-muted-foreground text-center">
                                    No chats available to add
                                </div>
                            ) : (
                                <div className="p-2">
                                    <TimeGroupedThreadList
                                        threadHistory={threadHistory!}
                                        sortedTimeGroupKeys={
                                            sortedTimeGroupKeys
                                        }
                                        folderedThreadIds={folderedThreadIds}
                                        getTimeGroupLabel={getTimeGroupLabel}
                                        isMobile={isMobile}
                                        isMultiSelectMode={true}
                                        selectedThreadIds={selectedThreadIds}
                                        onToggleSelection={
                                            onToggleThreadSelection
                                        }
                                        showTooltips={false}
                                        showThreadOptions={false}
                                        threadIdToRename={threadIdToRename}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button size="medium"
                            type="button"
                            variant="ghost"
                            onClick={onCloseMultiSelectDialog}
                        >
                            Cancel
                        </Button>
                        <Button size="medium"
                            type="button"
                            onClick={onAssignSelectedThreads}
                            disabled={selectedThreadIds.size === 0}
                        >
                            Add {selectedThreadIds.size} chat
                            {selectedThreadIds.size !== 1 ? 's' : ''}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Thread delete/rename dialogs */}
            <AlertDialog
                open={!!threadIdToDelete}
                onOpenChange={() => setThreadIdToDelete(undefined)}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            <Translator path="threadHistory.thread.actions.delete.title" />
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            <Translator path="threadHistory.thread.actions.delete.description" />
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="flex-row gap-2 sm:gap-0">
                        <AlertDialogCancel className="mt-0">
                            <Translator path="common.actions.cancel" />
                        </AlertDialogCancel>
                        <AlertDialogAction variant="destructive" onClick={onDeleteThread}>
                                <Translator path="common.actions.confirm" />
                            </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <Dialog
                open={!!threadIdToRename}
                onOpenChange={() => setThreadIdToRename(undefined)}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            <Translator path="threadHistory.thread.actions.rename.title" />
                        </DialogTitle>
                        <DialogDescription>
                            <Translator path="threadHistory.thread.actions.rename.description" />
                        </DialogDescription>
                    </DialogHeader>
                    <div className="my-6">
                        <Label htmlFor="name" className="text-right">
                            <Translator path="threadHistory.thread.actions.rename.form.name.label" />
                        </Label>
                        <Input
                            id="name"
                            required
                            value={threadNewName}
                            onChange={(e) => setThreadNewName(e.target.value)}
                            placeholder="Enter thread name"
                            autoFocus
                        />
                    </div>
                    <DialogFooter>
                        <Button size="medium"
                            type="button"
                            variant="ghost"
                            onClick={() => setThreadIdToRename(undefined)}
                        >
                            <Translator path="common.actions.cancel" />
                        </Button>
                        <Button size="medium" type="button" onClick={onRenameThread}>
                            <Translator path="common.actions.confirm" />
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Share Thread Dialog */}
            <Dialog
                open={!!threadIdToShare}
                onOpenChange={() => setThreadIdToShare(undefined)}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Share thread</DialogTitle>
                    </DialogHeader>
                    <div className="my-6">
                        <Label htmlFor="share-url" className="text-right">
                            Thread URL
                        </Label>
                        <div className="flex justify-center items-center gap-1 mt-1">
                            <Input
                                id="share-url"
                                value={shareUrl}
                                readOnly
                                className="flex-1"
                                onClick={(e) => e.currentTarget.select()}
                            />
                            <Button
                                type="button"
                                onClick={onCopyShareUrl}
                                variant="ghost"
                                className="cursor-pointer"
                                disabled={!shareUrl}
                            >
                                <Copy />
                            </Button>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button size="medium"
                            type="button"
                            onClick={() => setThreadIdToShare(undefined)}
                        >
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
